import { useEffect, useRef } from 'react';

const keyOf = (ingestId: string) => `doc-ingest-draft:${ingestId}`;

export function loadDraft<T>(ingestId: string): T | null {
  try {
    const raw = localStorage.getItem(keyOf(ingestId));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

// Autosave: writes on every change of `draft` (cheap JSON).
export function useDraftAutosave<T>(ingestId: string, draft: T) {
  const idRef = useRef(ingestId);
  // Once clearDraft() fires (e.g. on a successful accept) the autosave must NOT
  // re-write the draft we just removed. The post-accept refetch re-renders this
  // still-mounted component with a fresh `draft` object literal, which would
  // otherwise resurrect the deleted key. A `cleared` latch blocks further writes
  // until the document (ingestId) actually changes.
  const clearedRef = useRef(false);
  if (idRef.current !== ingestId) {
    idRef.current = ingestId;
    clearedRef.current = false; // new document → autosave is live again
  }
  useEffect(() => {
    if (clearedRef.current) return;
    try {
      localStorage.setItem(keyOf(ingestId), JSON.stringify(draft));
    } catch {
      // storage full / unavailable — autosave is best-effort, never block edits.
    }
  }, [ingestId, draft]);

  function clearDraft() {
    clearedRef.current = true;
    try {
      localStorage.removeItem(keyOf(idRef.current));
    } catch {
      /* noop */
    }
  }
  return { clearDraft };
}
